<template>
  <div id="app">
    <div class="background-noise"></div>
    <div class="top-nav">
      <SideNav :sections="sections" :activeSection="activeSection" />
      <ThemeToggle :theme="theme" @toggle-theme="toggleTheme" />
    </div>
    <main class="content-wrapper">
      <HeroSection />
      <AboutMe />
      <SkillsSection />
      <ProjectsSection />
      <ContactSection />
    </main>
  </div>
</template>

<script>
import ThemeToggle from "./components/ThemeToggle.vue";
import SideNav from "./components/SideNav.vue";
import HeroSection from "./components/HeroSection.vue";
import AboutMe from "./components/AboutMe.vue";
import SkillsSection from "./components/SkillsSection.vue";
import ProjectsSection from "./components/ProjectsSection.vue";
import ContactSection from "./components/ContactSection.vue";

export default {
  name: "App",
  components: {
    ThemeToggle,
    SideNav,
    HeroSection,
    AboutMe,
    SkillsSection,
    ProjectsSection,
    ContactSection,
  },
  data() {
    return {
      theme: "light",
      activeSection: "hero",
      sections: [
        { id: "about", name: "About" },
        { id: "skills", name: "Skills" },
        { id: "projects", name: "Projects" },
        { id: "contact", name: "Contact" },
      ],
    };
  },
  methods: {
    handleScroll() {
      const scrollPosition = window.scrollY + window.innerHeight * 0.3;
      for (const section of this.sections.slice().reverse()) {
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition) {
          this.activeSection = section.id;
          return;
        }
      }
      if (window.scrollY < window.innerHeight / 2) this.activeSection = "hero";
    },
    toggleTheme() {
      const newTheme = this.theme === "light" ? "dark" : "light";
      this.theme = newTheme;
      localStorage.setItem("theme", newTheme);
      document.body.setAttribute("data-theme", newTheme);
    },
    setTheme(theme) {
      this.theme = theme;
      document.body.setAttribute("data-theme", theme);
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    const savedTheme = localStorage.getItem("theme") || "light";
    this.setTheme(savedTheme);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
};
</script>

<style>
@import url("https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.8/dist/web/static/pretendard.css");
@import url("https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap");

:root {
  /* Modern Color Palette */
  --bg-color: #f8f9fa;
  --bg-secondary: #ffffff;
  --text-primary: #111827;
  --text-secondary: #6b7280;
  --text-tertiary: #9ca3af;
  --border-color: #e5e7eb;
  --accent-color: #4f46e5; /* Indigo */
  --accent-glow: rgba(79, 70, 229, 0.15);
  --card-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

[data-theme="dark"] {
  --bg-color: #0f172a; /* Slate 900 */
  --bg-secondary: #1e293b; /* Slate 800 */
  --text-primary: #f8fafc;
  --text-secondary: #94a3b8;
  --text-tertiary: #64748b;
  --border-color: #334155;
  --accent-color: #818cf8; /* Light Indigo */
  --accent-glow: rgba(129, 140, 248, 0.15);
  --card-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
}

body {
  background-color: var(--bg-color);
  color: var(--text-primary);
  font-family: "Pretendard", -apple-system, BlinkMacSystemFont, system-ui,
    sans-serif;
  margin: 0;
  line-height: 1.6;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.code-font {
  font-family: "JetBrains Mono", monospace;
}

h1,
h2,
h3,
h4,
h5 {
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.02em;
}

section {
  padding: 120px 5%;
  max-width: 1200px;
  margin: 0 auto;
}

.top-nav {
  position: fixed;
  top: 30px;
  right: 30px;
  z-index: 1000;
  display: flex;
  gap: 20px;
  align-items: center;
}

/* Background Noise Texture for texture */
.background-noise {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
}

@media (max-width: 768px) {
  .top-nav {
    right: 20px;
    top: 20px;
  }
  section {
    padding: 80px 5%;
  }
}
</style>
